import React from 'react';
import { Trash2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useBuilderStore } from '../../store/builderStore';
import type { ComponentType } from '../../types/builder';
import { PictureSettings } from './Settings/PictureSettings';
import { PictureSliderSettings } from './Settings/PictureSliderSettings';
import { FormSettings } from './Settings/FormSettings';
import { VideoSettings } from './Settings/VideoSettings';
import { OnlineUsersSettings } from './Settings/OnlineUsersSettings';
import { RecentWithdrawalsSettings } from './Settings/RecentWithdrawalsSettings';
import { StatsBoxSettings } from './Settings/StatsBoxSettings';
import { JackpotSettings } from './Settings/JackpotSettings';
import { CountdownTimerSettings } from './Settings/CountdownTimerSettings';
import { BannerSlideSettings } from './Settings/BannerSlideSettings';
import { GameRTPSettings } from './Settings/GameRTPSettings';
import { LinkSettings } from './Settings/LinkSettings';
import { ProfileSettings } from './Settings/ProfileSettings';
import { NameSettings } from './Settings/NameSettings';
import { BioSettings } from './Settings/BioSettings';
import { BrandLogoSettings } from './Settings/BrandLogoSettings';
import { PartnerLogosSettings } from './Settings/PartnerLogosSettings';
import { LeaderboardSettings } from './Settings/LeaderboardSettings';
import { FAQSettings } from './Settings/FAQSettings';
import { SocialIconsSettings } from './Settings/SocialIconsSettings';
import { TestimonialSettings } from './Settings/TestimonialSettings';
import { GoogleReviewsSettings } from './Settings/GoogleReviewsSettings';
import { SurveySettings } from './Settings/SurveySettings';
import { MusicPlayerSettings } from './Settings/MusicPlayerSettings';
import { SpinWheelSettings } from './Settings/SpinWheelSettings';

export const SettingsPanel: React.FC = () => {
  const { t } = useTranslation('builder');
  const { selectedComponent, deleteComponent, selectComponent, updateComponent } = useBuilderStore();

  // Mobile behaviour:
  //   • When nothing is selected, the panel collapses entirely
  //     (no overlay, no slide-out arrow) so the canvas gets the full
  //     viewport. Selecting a component on canvas re-mounts and slides
  //     it back in.
  //   • On lg+ we keep the historic "empty state" so the panel always
  //     occupies its column — designers expect a stable 3-pane layout.
  if (!selectedComponent) {
    return (
      <div className="theme-fixed hidden lg:flex w-80 bg-gray-900 text-white p-6 items-center justify-center">
        <p className="text-gray-400 text-sm text-center">
          {t('settingsPanel.emptyState')}
        </p>
      </div>
    );
  }

  const handleDelete = () => {
    if (confirm(t('settingsPanel.deleteConfirm'))) {
      deleteComponent(selectedComponent.id);
      selectComponent(null);
    }
  };

  const handleUpdate = (settings: any) => {
    updateComponent(selectedComponent.id, { settings });
  };

  const renderSettings = () => {
    switch (selectedComponent.type) {
      case 'picture':
        return <PictureSettings component={selectedComponent} />;
      case 'pictureSlider':
        return <PictureSliderSettings component={selectedComponent} />;
      case 'form':
        return <FormSettings component={selectedComponent} />;
      case 'video':
        return <VideoSettings component={selectedComponent} />;
      case 'onlineUsers':
        return <OnlineUsersSettings component={selectedComponent} />;
      case 'recentWithdrawals':
        return <RecentWithdrawalsSettings component={selectedComponent} />;
      case 'statsBox':
        return <StatsBoxSettings component={selectedComponent} />;
      case 'jackpot':
        return <JackpotSettings component={selectedComponent} onUpdate={handleUpdate} />;
      case 'countdownTimer':
        return <CountdownTimerSettings component={selectedComponent} onUpdate={handleUpdate} />;
      case 'bannerSlide':
        return <BannerSlideSettings component={selectedComponent} onUpdate={handleUpdate} />;
      case 'gameRTP':
        return <GameRTPSettings component={selectedComponent} onUpdate={handleUpdate} />;
      case 'link':
        return <LinkSettings component={selectedComponent} onUpdate={handleUpdate} />;
      case 'profile':
        return <ProfileSettings component={selectedComponent} />;
      case 'name':
        return <NameSettings component={selectedComponent} />;
      case 'bio':
        return <BioSettings component={selectedComponent} />;
      case 'brandLogo':
        return <BrandLogoSettings component={selectedComponent} />;
      case 'partnerLogos':
        return <PartnerLogosSettings component={selectedComponent} />;
      case 'leaderboard':
        return <LeaderboardSettings component={selectedComponent} />;
      case 'faq':
        return <FAQSettings component={selectedComponent} />;
      case 'socialIcons':
        return <SocialIconsSettings component={selectedComponent} />;
      case 'testimonial':
        return <TestimonialSettings component={selectedComponent} />;
      case 'googleReviews':
        return <GoogleReviewsSettings component={selectedComponent} />;
      case 'survey':
        return <SurveySettings component={selectedComponent} onUpdate={handleUpdate} />;
      case 'musicPlayer':
        return <MusicPlayerSettings component={selectedComponent} onUpdate={handleUpdate} />;
      case 'spinWheel':
        return <SpinWheelSettings component={selectedComponent} />;
      default:
        return null;
    }
  };

  // Resolve the localized component type label via i18n. Falls back to
  // the raw type key when no translation exists.
  const typeKey: ComponentType = selectedComponent.type;
  const i18nKey = `settingsPanel.componentTypes.${typeKey}` as const;
  const translatedType = t(i18nKey);
  const componentLabel = translatedType === i18nKey ? typeKey : translatedType;

  return (
    <>
      {/* Mobile backdrop — tapping the canvas area dismisses the
          inspector by clearing the selection. */}
      <button
        type="button"
        aria-label={t('settingsPanel.close', { defaultValue: 'Close settings' })}
        onClick={() => selectComponent(null)}
        className="lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
      />

      <div
        className="
          theme-fixed fixed inset-y-0 right-0 z-50 w-[88vw] max-w-sm bg-gray-900 text-white overflow-y-auto shadow-2xl border-l border-gray-800
          lg:static lg:z-auto lg:w-80 lg:max-w-none lg:shadow-none lg:border-l-0
        "
      >
        <div className="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2">
            <h2 className="text-base sm:text-lg font-semibold truncate">{t('settingsPanel.heading')}</h2>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={handleDelete}
                className="p-2 hover:bg-red-600 rounded-lg transition-colors"
                title={t('settingsPanel.deleteTitle')}
              >
                <Trash2 size={18} />
              </button>
              {/* Mobile close — clears selectedComponent so the panel
                  unmounts (returns to canvas-only view). On lg+ this is
                  hidden because the panel is part of the static layout. */}
              <button
                type="button"
                onClick={() => selectComponent(null)}
                className="lg:hidden p-2 hover:bg-gray-800 rounded-lg transition-colors"
                title={t('settingsPanel.close', { defaultValue: 'Close' })}
              >
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="mb-4 sm:mb-6">
            <h3 className="text-base font-medium mb-4">
              {componentLabel}
            </h3>
          </div>

          {renderSettings()}
        </div>
      </div>
    </>
  );
};
